// Anthropic Service Health Dashboard - production page
// Data: live from status.claude.com API + S3/CloudFront cache for history (YTD / long periods)
import { useState, useEffect, useCallback } from 'react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell,
} from 'recharts'
import Alert from '@cloudscape-design/components/alert'
import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import Container from '@cloudscape-design/components/container'
import ColumnLayout from '@cloudscape-design/components/column-layout'
import Flashbar from '@cloudscape-design/components/flashbar'
import Header from '@cloudscape-design/components/header'
import Spinner from '@cloudscape-design/components/spinner'
import SpaceBetween from '@cloudscape-design/components/space-between'
import StatusIndicator from '@cloudscape-design/components/status-indicator'
import Table from '@cloudscape-design/components/table'
import TextFilter from '@cloudscape-design/components/text-filter'
import Select from '@cloudscape-design/components/select'
import {
  colorTextBodyDefault,
  colorTextBodySecondary,
  colorBackgroundContainerContent,
  colorBorderDividerDefault,
  colorTextStatusError,
  colorTextStatusSuccess,
  colorTextAccent,
} from '@cloudscape-design/design-tokens'

const isModelEntity = (e: string) => /^\w+\s+[\d.]+$/.test(e)

// Returns true if the model was active at any point during the given window.
// Uses MODEL_WINDOWS (lifecycle-aware) + recency heuristic for pre-lifecycle models.
// Non-model entities (Claude.ai, Claude API, etc.) always return true.
// lastIncidentMs: timestamp of the entity's most recent incident (for recency heuristic)
function modelActiveInWindow(
  entity: string,
  winStart: Date,
  winEnd: Date,
  lastIncidentMs?: number,
): boolean {
  if (!isModelEntity(entity)) return true // services always show

  const w = MODEL_WINDOWS[entity]
  if (!w) {
    // Not in MODEL_WINDOWS — either new (active) or very old (retired)
    // Use recency heuristic: if most recent incident is > 18 months ago, treat as retired
    if (lastIncidentMs !== undefined) {
      const age = Date.now() - lastIncidentMs
      if (age > RETIRED_RECENCY_CUTOFF_MS) return false // too old, hide from entity cards
    }
    return true // new model with no EOL yet
  }

  const launchMs = new Date(w[0]).getTime()
  const eolMs = w[1] ? new Date(w[1]).getTime() : Infinity
  // Show if the model's active period overlaps the selected window
  return winEnd.getTime() > launchMs && winStart.getTime() < eolMs
}

// ── Cache & lifecycle config ──────────────────────────────────────────────────
const LIVE_API = 'https://status.claude.com/api/v2/incidents.json'
// CloudFront URL serving the S3 cache (updated hourly by claude-cache-updater Lambda)
const CACHE_URL = 'https://d3f9i740suk81r.cloudfront.net/claude-incidents-cache.json'
// Periods that need historical data beyond what the live API returns (~50 incidents)
const NEEDS_CACHE = (period: string) => period === '0' // YTD only for now

// ── Model active windows ──────────────────────────────────────────────────────
// Source of truth: backend/model-lifecycle.json (EOL dates from AWS Bedrock docs)
// Format: [launchDate, eolDate | null]  — null means still active / no EOL announced
// Models NOT listed here are either:
//   a) New (no EOL yet) → treated as active
//   b) So old they predate the lifecycle page → treated as retired (recency heuristic below)
const MODEL_WINDOWS: Record<string, [string, string | null]> = {
  // Active models (no EOL announced)
  'Opus 4.7':        ['2026-04-01', null],
  'Opus 4.6':        ['2026-02-01', null],
  'Sonnet 4.6':      ['2026-02-01', null],
  'Haiku 4.5':       ['2025-10-01', null],
  // Models with known EOL dates (from model-lifecycle.json)
  'Sonnet 3.7':      ['2025-02-19', '2026-04-28'],  // eolDate from lifecycle JSON
  'Haiku 3.5':       ['2024-10-22', '2026-06-19'],  // eolDate from lifecycle JSON
  'Sonnet 3.6':      ['2024-06-20', '2026-07-30'],  // maps to 3.5 v1 eolDate
  'Sonnet 3.5':      ['2024-06-20', '2026-07-30'],  // eolDate from lifecycle JSON
  'Haiku 3':         ['2024-03-07', '2026-09-10'],  // eolDate from lifecycle JSON
  'Sonnet 3':        ['2024-03-01', '2026-07-30'],
  // Retired models (EOL already passed — show in table only, not entity cards)
  'Sonnet 4.5':      ['2025-09-01', '2026-02-01'],
  'Opus 4.5':        ['2025-11-01', '2026-02-01'],
  'Opus 4.1':        ['2025-08-05', '2026-02-01'],
  'Opus 4':          ['2025-05-14', '2026-02-01'],
  'Sonnet 4':        ['2025-05-14', '2026-02-01'],
  'Sonnet 1':        ['2024-03-01', '2025-07-21'],
  'Sonnet 0620':     ['2024-06-20', '2025-10-28'],
  'Sonnet 20240620': ['2024-06-20', '2025-10-28'],
}

// Cutoff: models whose most recent incident is older than this are treated as retired
// even if not listed in MODEL_WINDOWS (handles pre-lifecycle-page models like Claude 1/2)
const RETIRED_RECENCY_CUTOFF_MS = 18 * 30 * 24 * 60 * 60 * 1000 // ~18 months

const COMPONENT_MAP: Record<string, string> = {
  'claude.ai': 'Claude.ai',
  'Claude Code': 'Claude Code',
  'platform.claude.com (formerly console.anthropic.com)': 'Console',
  'Claude API (api.anthropic.com)': 'Claude API',
  'Claude for Government': 'Claude for Gov',
  'Claude Cowork': 'Cowork',
}

const ENTITY_COLORS: Record<string, string> = {
  'Opus 4.6': '#cba6f7', 'Sonnet 4.6': '#89b4fa', 'Haiku 4.5': '#f9e2af',
  'Claude.ai': '#94e2d5', 'Claude Code': '#fab387', 'Console': '#89dceb',
  'Claude API': '#a6e3a1', 'Cowork': '#f5c2e7', 'Claude Desktop': '#f38ba8',
  'Claude for Gov': '#b4befe',
}
const FALLBACK_PALETTE = ['#cba6f7','#89b4fa','#94e2d5','#fab387','#f5c2e7','#89dceb','#a6e3a1','#f9e2af','#b4befe','#f38ba8']
let paletteIdx = 0
function getColor(entity: string): string {
  if (ENTITY_COLORS[entity]) return ENTITY_COLORS[entity]
  const c = FALLBACK_PALETTE[paletteIdx++ % FALLBACK_PALETTE.length]
  ENTITY_COLORS[entity] = c
  return c
}

// ── Types ────────────────────────────────────────────────────────────────────
interface Incident {
  id: string
  title: string
  services: string[]
  severity: 'degraded' | 'down'
  startDate: Date
  endDate: Date | null
  endCapped: Date
  durationMs: number
  durationMin: number
  ongoing: boolean
  mtta: number | null
  shortlink: string
  apiStatus: string
  components: string[]
}

interface EntityMetric {
  entity: string
  count: number
  availability: number
  mttr: number
  mtta: number | null
  totalDowntimeMs: number
  longest: Incident | null
}

// ── Data parsing (ported from original dashboard) ────────────────────────────

// Normalize model name aliases so "Opus 4.0" and "Opus 4" map to the same entity
const MODEL_ALIASES: Record<string, string> = {
  'Opus 4.0':   'Opus 4',
  'Sonnet 4.0': 'Sonnet 4',
}
function normalizeModel(name: string): string {
  return MODEL_ALIASES[name] ?? name
}

function extractEntities(inc: any): string[] {
  const entities = new Set<string>()
  const name: string = inc.name || ''
  const toTitle = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
  const explicit = [...name.matchAll(/\b(Opus|Sonnet|Haiku)\s+([\d.]+)/gi)]
  explicit.forEach(m => entities.add(normalizeModel(toTitle(m[1]) + ' ' + m[2])))
  if (explicit.length === 0) {
    const allModels = [...name.matchAll(/\b(Opus|Sonnet|Haiku)\b/gi)]
    const allVersions = [...name.matchAll(/\b(\d+\.\d+)\b/g)]
    if (allVersions.length === 1 && allModels.length > 0)
      allModels.forEach(m => entities.add(normalizeModel(toTitle(m[1]) + ' ' + allVersions[0][1])))
  }
  ;(inc.components || []).forEach((c: any) => entities.add(COMPONENT_MAP[c.name] || c.name))
  const lower = name.toLowerCase()
  if (lower.includes('cowork') && !entities.has('Cowork')) entities.add('Cowork')
  if (lower.includes('desktop') && !entities.has('Claude Desktop')) entities.add('Claude Desktop')
  // Title-regex fallbacks for when cache JSON is missing `components` (90% of Anthropic
  // cache entries have components: []). Without these, incidents get `services: []` and
  // are silently dropped by the model-active filter. See Bug 3 in code review.
  if (lower.includes('claude.ai') && !entities.has('Claude.ai')) entities.add('Claude.ai')
  if (lower.includes('claude code') && !entities.has('Claude Code')) entities.add('Claude Code')
  if ((lower.includes('console') || lower.includes('platform.claude')) && !entities.has('Console')) entities.add('Console')
  if ((lower.includes('api.anthropic') || /\bclaude api\b/.test(lower)) && !entities.has('Claude API')) entities.add('Claude API')
  if ((lower.includes('government') || /\bgov\b/.test(lower)) && !entities.has('Claude for Gov')) entities.add('Claude for Gov')
  // Final fallback: if nothing matched, bucket into 'Claude API' so the incident still
  // shows up in the table/KPIs rather than being filtered out entirely.
  if (entities.size === 0) entities.add('Claude API')
  return [...entities]
}

function computeMtta(inc: any): number | null {
  const updates = (inc.incident_updates || []).slice().reverse()
  if (updates.length < 2) return null
  const firstInv = updates.find((u: any) => u.status === 'investigating')
  if (!firstInv) return null
  const invTime = new Date(firstInv.created_at).getTime()
  const target = updates.find((u: any) =>
    (u.status === 'identified' || u.status === 'monitoring') &&
    new Date(u.created_at).getTime() >= invTime
  )
  if (target) return Math.round((new Date(target.created_at).getTime() - invTime) / 60000)
  if (inc.resolved_at) return Math.round((new Date(inc.resolved_at).getTime() - invTime) / 60000)
  return null
}

function parseIncidents(raw: any[]): Incident[] {
  return raw
    .filter((inc) => {
      // Drop ghost incidents: resolved_at present but <= started_at.
      // These are data artifacts that would otherwise masquerade as ongoing incidents
      // and inflate counts. Genuinely ongoing incidents have resolved_at === null.
      if (!inc.resolved_at) return true
      const s = new Date(inc.started_at || inc.created_at).getTime()
      const e = new Date(inc.resolved_at).getTime()
      return e > s
    })
    .map((inc, idx) => {
      const services = extractEntities(inc)
      const severity: 'down' | 'degraded' = inc.impact === 'critical' || inc.impact === 'major' ? 'down' : 'degraded'
      const startDate = new Date(inc.started_at || inc.created_at)
      const endDate = inc.resolved_at ? new Date(inc.resolved_at) : null
      const endCapped = endDate ?? new Date()
      const durationMs = endCapped.getTime() - startDate.getTime()
      return {
        id: inc.id,
        title: inc.name,
        services,
        severity,
        startDate,
        endDate,
        endCapped,
        durationMs,
        durationMin: durationMs / 60000,
        ongoing: !endDate,
        mtta: computeMtta(inc),
        shortlink: inc.shortlink || '',
        apiStatus: inc.status || '',
        components: (inc.components || []).map((c: any) => COMPONENT_MAP[c.name] || c.name),
      }
    }).sort((a, b) => a.startDate.getTime() - b.startDate.getTime())
}

function mergeIntervals(intervals: [number, number][]): [number, number][] {
  if (!intervals.length) return []
  const sorted = intervals.slice().sort((a, b) => a[0] - b[0])
  const merged: [number, number][] = [sorted[0]]
  for (let i = 1; i < sorted.length; i++) {
    const last = merged[merged.length - 1]
    if (sorted[i][0] <= last[1]) last[1] = Math.max(last[1], sorted[i][1])
    else merged.push(sorted[i])
  }
  return merged
}

function formatDuration(ms: number): string {
  const totalMin = Math.round(ms / 60000)
  if (totalMin < 60) return totalMin + 'm'
  const h = Math.floor(totalMin / 60), m = totalMin % 60
  if (h < 24) return h + 'h ' + m + 'm'
  return Math.floor(h / 24) + 'd ' + (h % 24) + 'h'
}

function fmtDateShort(d: Date): string {
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' })
}

// ── Shared chart tooltip style ────────────────────────────────────────────────
const tooltipStyle = {
  backgroundColor: colorBackgroundContainerContent,
  border: `1px solid ${colorBorderDividerDefault}`,
  borderRadius: 6, fontSize: 12, color: colorTextBodyDefault,
}

// ── Availability ring ─────────────────────────────────────────────────────────
function AvailRing({ pct }: { pct: number }) {
  const color = pct >= 99 ? colorTextStatusSuccess : pct >= 95 ? '#f97316' : colorTextStatusError
  const deg = (pct / 100) * 360
  return (
    <div style={{
      width: 84, height: 84, borderRadius: '50%',
      background: `conic-gradient(${color} ${deg}deg, ${colorBorderDividerDefault} ${deg}deg)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        width: 64, height: 64, borderRadius: '50%',
        background: colorBackgroundContainerContent,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 700, color,
      }}>
        {pct.toFixed(2)}%
      </div>
    </div>
  )
}

// ── KPI card ──────────────────────────────────────────────────────────────────
function KpiCard({ label, value, detail, onClick }: {
  label: string; value: string | number; detail: string; onClick?: () => void
}) {
  return (
    <div
      onClick={onClick}
      style={{
        background: colorBackgroundContainerContent,
        border: `1px solid ${colorBorderDividerDefault}`,
        borderRadius: 8, padding: '14px 16px', textAlign: 'center',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <Box variant="small" color="text-body-secondary">{label}</Box>
      <Box variant="h2">{value}</Box>
      <Box variant="small" color="text-body-secondary">{detail}</Box>
    </div>
  )
}

// ── Entity cards ──────────────────────────────────────────────────────────────
function EntityCards({ metrics }: { metrics: EntityMetric[] }) {
  if (!metrics.length) return null
  return (
    <Container header={<Header variant="h2">Claude Model &amp; Service Availability</Header>}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 }}>
        {metrics.map(m => (
          <div key={m.entity} style={{
            border: `1px solid ${colorBorderDividerDefault}`,
            borderRadius: 8, padding: 16, textAlign: 'center',
            background: colorBackgroundContainerContent,
          }}>
            <Box fontWeight="bold" fontSize="body-m">
              <span style={{ color: colorTextBodyDefault }}>{m.entity}</span>
            </Box>
            <div style={{ margin: '12px auto', width: 'fit-content' }}>
              <AvailRing pct={m.availability} />
            </div>
            <Box variant="small" color="text-body-secondary">
              Incidents: <strong style={{ color: colorTextBodyDefault }}>{m.count}</strong><br />
              Downtime: <strong style={{ color: colorTextBodyDefault }}>{formatDuration(m.totalDowntimeMs)}</strong><br />
              MTTR: <strong style={{ color: colorTextBodyDefault }}>{Math.round(m.mttr)}m</strong><br />
              MTTA: <strong style={{ color: colorTextBodyDefault }}>{m.mtta !== null ? Math.round(m.mtta) + 'm' : '-'}</strong>
            </Box>
          </div>
        ))}
      </div>
    </Container>
  )
}

// ── Availability chart ────────────────────────────────────────────────────────
function AvailabilityChart({ metrics }: { metrics: EntityMetric[] }) {
  const minVal = Math.max(80, Math.floor(Math.min(...metrics.map(m => m.availability))) - 1)
  return (
    <Container header={<Header variant="h3">Availability % by Entity</Header>}>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={metrics} margin={{ top: 8, right: 8, left: 0, bottom: 48 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={colorBorderDividerDefault} />
          <XAxis dataKey="entity" tick={{ fill: colorTextBodySecondary, fontSize: 10 }} angle={-30} textAnchor="end" interval={0} />
          <YAxis domain={[minVal, 100]} tickFormatter={v => v + '%'} tick={{ fill: colorTextBodySecondary, fontSize: 11 }} />
          <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [v.toFixed(2) + '%', 'Availability']} />
          <Bar dataKey="availability" radius={[4, 4, 0, 0]}>
            {metrics.map(m => <Cell key={m.entity} fill={getColor(m.entity) + 'cc'} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Container>
  )
}

// ── MTTR chart ────────────────────────────────────────────────────────────────
function MttrChart({ metrics }: { metrics: EntityMetric[] }) {
  return (
    <Container header={<Header variant="h3">Mean Time to Resolve (minutes)</Header>}>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={metrics} margin={{ top: 8, right: 8, left: 0, bottom: 48 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={colorBorderDividerDefault} />
          <XAxis dataKey="entity" tick={{ fill: colorTextBodySecondary, fontSize: 10 }} angle={-30} textAnchor="end" interval={0} />
          <YAxis tickFormatter={v => v + 'm'} tick={{ fill: colorTextBodySecondary, fontSize: 11 }} />
          <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [Math.round(v) + 'm', 'MTTR']} />
          <Bar dataKey="mttr" fill="#f9e2afcc" stroke="#f9e2af" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </Container>
  )
}

// ── Daily incident count ──────────────────────────────────────────────────────
function DailyChart({ dayCounts }: { dayCounts: { day: string; count: number }[] }) {
  return (
    <Container header={<Header variant="h3">Daily Incident Count</Header>}>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={dayCounts} margin={{ top: 8, right: 8, left: 0, bottom: 48 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={colorBorderDividerDefault} />
          <XAxis dataKey="day" tick={{ fill: colorTextBodySecondary, fontSize: 10 }} angle={-30} textAnchor="end" interval={0} />
          <YAxis allowDecimals={false} tick={{ fill: colorTextBodySecondary, fontSize: 11 }} />
          <Tooltip contentStyle={tooltipStyle} />
          <Bar dataKey="count" radius={[4, 4, 0, 0]}>
            {dayCounts.map(d => (
              <Cell key={d.day} fill={d.count === 0 ? '#a6e3a166' : d.count <= 2 ? '#f9e2af99' : '#f38ba899'} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Container>
  )
}

// ── Downtime chart ────────────────────────────────────────────────────────────
function DowntimeChart({ metrics }: { metrics: EntityMetric[] }) {
  const sorted = [...metrics].sort((a, b) => b.totalDowntimeMs - a.totalDowntimeMs)
  return (
    <Container header={<Header variant="h3">Total Downtime by Entity</Header>}>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={sorted} layout="vertical" margin={{ top: 8, right: 16, left: 64, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={colorBorderDividerDefault} />
          <XAxis type="number" tickFormatter={v => Math.round(v / 60) + 'h'} tick={{ fill: colorTextBodySecondary, fontSize: 11 }} />
          <YAxis type="category" dataKey="entity" tick={{ fill: colorTextBodySecondary, fontSize: 11 }} width={64} />
          <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [formatDuration(v), 'Downtime']} />
          <Bar dataKey="totalDowntimeMs" radius={[0, 4, 4, 0]}>
            {sorted.map(m => <Cell key={m.entity} fill={getColor(m.entity) + 'cc'} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Container>
  )
}

// ── Active incident banner ────────────────────────────────────────────────────
function ActiveBanner({ incidents }: { incidents: Incident[] }) {
  const active = incidents.filter(i =>
    i.apiStatus === 'investigating' || i.apiStatus === 'identified' || i.apiStatus === 'monitoring'
  )
  if (!active.length) return null
  return (
    <Flashbar items={active.map(i => ({
      type: i.severity === 'down' ? 'error' as const : 'warning' as const,
      header: `Active: ${i.title}`,
      content: `Affected: ${i.components.length ? i.components.join(', ') : i.services.join(', ')} · Running for ${formatDuration(Date.now() - i.startDate.getTime())}`,
      dismissible: false,
      id: i.id,
      action: i.shortlink
        ? <Button href={i.shortlink} target="_blank" variant="inline-link">View →</Button>
        : undefined,
    }))} />
  )
}

// ── Incident table ────────────────────────────────────────────────────────────
function IncidentTable({ incidents }: { incidents: Incident[] }) {
  const [filterText, setFilterText] = useState('')
  const [severityOption, setSeverityOption] = useState({ label: 'All Severities', value: '' })
  const [entityOption, setEntityOption] = useState({ label: 'All Entities', value: '' })
  const [currentPage, setCurrentPage] = useState(0)
  const PAGE_SIZE = 10

  const allEntities = [...new Set(incidents.flatMap(i => i.services))].sort()
  const entityOptions = [
    { label: 'All Entities', value: '' },
    ...allEntities.map(e => ({ label: e, value: e })),
  ]
  const severityOptions = [
    { label: 'All Severities', value: '' },
    { label: 'Degraded', value: 'degraded' },
    { label: 'Down', value: 'down' },
  ]

  const filtered = incidents.filter(i => {
    if (filterText && !i.title.toLowerCase().includes(filterText.toLowerCase()) &&
        !i.services.join(' ').toLowerCase().includes(filterText.toLowerCase())) return false
    if (severityOption.value && i.severity !== severityOption.value) return false
    if (entityOption.value && !i.services.includes(entityOption.value)) return false
    return true
  }).slice().reverse() // newest first

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const page = filtered.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE)

  return (
    <Table
      columnDefinitions={[
        {
          id: 'title', header: 'Title', width: 300,
          cell: (item: Incident) => item.shortlink
            ? <a href={item.shortlink} target="_blank" rel="noopener" style={{ color: colorTextAccent }}>{item.title}</a>
            : item.title,
        },
        {
          id: 'services', header: 'Entities',
          cell: (item: Incident) => (
            <SpaceBetween direction="horizontal" size="xxs">
              {item.services.map(s => (
                <span key={s} style={{
                  display: 'inline-block', padding: '1px 8px', borderRadius: 10,
                  fontSize: 11, fontWeight: 600,
                  background: getColor(s) + '22', color: getColor(s),
                }}>{s}</span>
              ))}
            </SpaceBetween>
          ),
        },
        {
          id: 'severity', header: 'Severity',
          cell: (item: Incident) => (
            <StatusIndicator type={item.severity === 'down' ? 'error' : 'warning'}>
              {item.severity === 'down' ? 'Down' : 'Degraded'}
            </StatusIndicator>
          ),
        },
        { id: 'start', header: 'Start', cell: (item: Incident) => fmtDateShort(item.startDate) },
        {
          id: 'end', header: 'End',
          cell: (item: Incident) => item.ongoing
            ? <StatusIndicator type="in-progress">Ongoing</StatusIndicator>
            : fmtDateShort(item.endDate!),
        },
        {
          id: 'duration', header: 'Duration',
          cell: (item: Incident) => formatDuration(item.durationMs) + (item.ongoing ? '+' : ''),
        },
        {
          id: 'mtta', header: 'Time to Ack',
          cell: (item: Incident) => item.mtta !== null ? Math.round(item.mtta) + 'm' : '-',
        },
      ]}
      items={page}
      filter={
        <SpaceBetween direction="horizontal" size="xs">
          <TextFilter
            filteringText={filterText}
            filteringPlaceholder="Search incidents…"
            onChange={({ detail }) => { setFilterText(detail.filteringText); setCurrentPage(0) }}
          />
          <Select
            selectedOption={severityOption}
            options={severityOptions}
            onChange={({ detail }) => { setSeverityOption(detail.selectedOption as any); setCurrentPage(0) }}
          />
          <Select
            selectedOption={entityOption}
            options={entityOptions}
            onChange={({ detail }) => { setEntityOption(detail.selectedOption as any); setCurrentPage(0) }}
          />
        </SpaceBetween>
      }
      header={
        <Header
          counter={`(${filtered.length})`}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button disabled={currentPage === 0} onClick={() => setCurrentPage(0)}>«</Button>
              <Button disabled={currentPage === 0} onClick={() => setCurrentPage(p => p - 1)}>‹ Prev</Button>
              <Box padding={{ top: 'xs' }} color="text-body-secondary" fontSize="body-s">
                {currentPage + 1} / {totalPages}
              </Box>
              <Button disabled={currentPage >= totalPages - 1} onClick={() => setCurrentPage(p => p + 1)}>Next ›</Button>
              <Button disabled={currentPage >= totalPages - 1} onClick={() => setCurrentPage(totalPages - 1)}>»</Button>
            </SpaceBetween>
          }
        >
          Incident Log
        </Header>
      }
      stripedRows
      sortingDisabled
      variant="container"
      empty={<Box textAlign="center" color="text-body-secondary">No incidents match your filters</Box>}
    />
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────
export function AnthropicHealthPage() {
  const [allIncidents, setAllIncidents] = useState<Incident[]>([])
  const [allEntities, setAllEntities] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [period, setPeriod] = useState<'7' | '14' | '30' | '0'>('14')
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')

  // ── Fetch & merge (live API + S3 cache for long periods) ──────────────────
  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      // Always fetch live API
      const res = await fetch(LIVE_API)
      if (!res.ok) throw new Error('Could not reach status.claude.com API')
      const liveData = await res.json()
      const liveIncidents = parseIncidents(liveData.incidents || [])

      // For YTD, also fetch the S3 cache and merge (ID-based dedup, live wins)
      let merged = liveIncidents
      if (NEEDS_CACHE(period)) {
        try {
          const cacheRes = await fetch(CACHE_URL)
          if (cacheRes.ok) {
            const cacheData = await cacheRes.json()
            const cacheIncidents = parseIncidents(Array.isArray(cacheData) ? cacheData : (cacheData.incidents || []))
            // Merge: start with cache, overlay live (live data is more up-to-date)
            const mergeMap = new Map<string, Incident>()
            cacheIncidents.forEach(i => mergeMap.set(i.id, i))
            liveIncidents.forEach(i => mergeMap.set(i.id, i)) // live overwrites cache
            merged = [...mergeMap.values()].sort((a, b) => a.startDate.getTime() - b.startDate.getTime())
          }
        } catch (cacheErr) {
          // Cache fetch failed — continue with live data only
          console.warn('[AnthropicHealth] Cache fetch failed, using live API only:', cacheErr)
        }
      }

      setAllIncidents(merged)

      // Build global entity list sorted: models first (by family/version), then services by count
      const counts: Record<string, number> = {}
      merged.forEach(inc => inc.services.forEach(e => { counts[e] = (counts[e] || 0) + 1 }))
      const isModel = (n: string) => /^\w+\s+[\d.]+$/.test(n)
      const familyOrder: Record<string, number> = { opus: 0, sonnet: 1, haiku: 2 }
      const entities = Object.keys(counts).sort((a, b) => {
        const am = isModel(a), bm = isModel(b)
        if (am && !bm) return -1
        if (!am && bm) return 1
        if (am && bm) {
          const af = a.split(' ')[0].toLowerCase(), bf = b.split(' ')[0].toLowerCase()
          if (af !== bf) return (familyOrder[af] ?? 9) - (familyOrder[bf] ?? 9)
          return parseFloat(b.split(' ')[1]) - parseFloat(a.split(' ')[1])
        }
        return counts[b] - counts[a]
      })
      setAllEntities(entities)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [period])

  useEffect(() => { load() }, [load])

  // ── Window calculation ─────────────────────────────────────────────────────
  const { windowStart, windowEnd } = (() => {
    const now = new Date()
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1))
    if (customStart && customEnd) {
      return { windowStart: new Date(customStart + 'T00:00:00Z'), windowEnd: new Date(customEnd + 'T00:00:00Z') }
    }
    if (period === '0') {
      return { windowStart: new Date(Date.UTC(now.getUTCFullYear(), 0, 1)), windowEnd: end }
    }
    return { windowStart: new Date(end.getTime() - parseInt(period) * 86400000), windowEnd: end }
  })()

  // ── Filter incidents to window - only include relevant models ─────────────
  // Build a map of entity → most recent incident timestamp (for recency heuristic)
  const entityLastIncident: Record<string, number> = {}
  allIncidents.forEach(inc => {
    inc.services.forEach(s => {
      const t = inc.startDate.getTime()
      if (!entityLastIncident[s] || t > entityLastIncident[s]) entityLastIncident[s] = t
    })
  })

  const incidents = allIncidents.filter(inc => {
    const end = inc.endDate || windowEnd
    if (!(end > windowStart && inc.startDate < windowEnd)) return false
    // Keep incident if at least one of its services is active in this window
    return inc.services.some(s => modelActiveInWindow(s, windowStart, windowEnd, entityLastIncident[s]))
  }).map(inc => {
    const endCapped = new Date(Math.min((inc.endDate || windowEnd).getTime(), windowEnd.getTime()))
    const startClamped = new Date(Math.max(inc.startDate.getTime(), windowStart.getTime()))
    // Guard against ghost incidents resolved in milliseconds or negative clamped durations
    const durationMs = Math.max(0, endCapped.getTime() - startClamped.getTime())
    return { ...inc, endCapped, durationMs, durationMin: durationMs / 60000 }
  }).filter(inc => inc.durationMs > 10000) // drop incidents under 10 seconds - data artifacts

  const windowMs = windowEnd.getTime() - windowStart.getTime()
  const windowDays = windowMs / 86400000

  // ── Entity metrics ─────────────────────────────────────────────────────────
  const entityMetrics: EntityMetric[] = allEntities
    .filter(entity => modelActiveInWindow(entity, windowStart, windowEnd, entityLastIncident[entity]))
    .map(entity => {
    const eInc = incidents.filter(i => i.services.includes(entity))
    const outageInc = eInc.filter(i => i.severity === 'down')
    const intervals = outageInc.map(i => [
      Math.max(i.startDate.getTime(), windowStart.getTime()),
      i.endCapped.getTime(),
    ] as [number, number])
    const merged = mergeIntervals(intervals)
    const totalDowntimeMs = merged.reduce((s, iv) => s + (iv[1] - iv[0]), 0)
    const availability = ((windowMs - totalDowntimeMs) / windowMs) * 100
    const mttr = eInc.length > 0 ? eInc.reduce((s, i) => s + i.durationMin, 0) / eInc.length : 0
    const mttaIncs = eInc.filter(i => i.mtta !== null)
    const mtta = mttaIncs.length > 0 ? mttaIncs.reduce((s, i) => s + i.mtta!, 0) / mttaIncs.length : null
    const longest = eInc.length > 0 ? eInc.reduce((b, i) => i.durationMin > b.durationMin ? i : b) : null
    return { entity, count: eInc.length, availability, mttr, mtta, totalDowntimeMs, longest }
  })

  // ── Global KPIs ────────────────────────────────────────────────────────────
  const downIncidents = incidents.filter(i => i.severity === 'down')
  const allIntervals = downIncidents.map(i => [
    Math.max(i.startDate.getTime(), windowStart.getTime()),
    i.endCapped.getTime(),
  ] as [number, number])
  const totalDowntimeMs = mergeIntervals(allIntervals).reduce((s, iv) => s + (iv[1] - iv[0]), 0)
  const globalMttr = incidents.length > 0 ? incidents.reduce((s, i) => s + i.durationMin, 0) / incidents.length : 0
  const mttaIncs = incidents.filter(i => i.mtta !== null)
  const globalMtta = mttaIncs.length > 0 ? mttaIncs.reduce((s, i) => s + i.mtta!, 0) / mttaIncs.length : 0
  const globalLongest = incidents.length > 0 ? incidents.reduce((b, i) => i.durationMin > b.durationMin ? i : b) : null

  const dayCounts: { day: string; count: number }[] = []
  for (let d = new Date(windowStart); d < windowEnd; d = new Date(d.getTime() + 86400000)) {
    const dk = d.toISOString().slice(0, 10)
    dayCounts.push({ day: dk.slice(5), count: incidents.filter(i => i.startDate.toISOString().slice(0, 10) === dk).length })
  }
  const worstDay = [...dayCounts].sort((a, b) => b.count - a.count)[0] ?? { day: '-', count: 0 }

  const fmtDate = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ padding: 48, textAlign: 'center' }}>
      <SpaceBetween size="m" alignItems="center">
        <Spinner size="large" />
        <Box color="text-body-secondary">Fetching incidents from status.claude.com…</Box>
      </SpaceBetween>
    </div>
  )

  if (error) return (
    <div style={{ padding: 24 }}>
      <Alert type="error" header="Failed to load incident data" action={<Button onClick={load}>Retry</Button>}>
        {error}
      </Alert>
    </div>
  )

  return (
    <div style={{ padding: 24 }}>
      <SpaceBetween size="l">

        {/* Header */}
        <Header
          variant="h1"
          description={
            <span>
              <span style={{ display: 'block', fontSize: 12, color: colorTextBodySecondary }}>{fmtDate(windowStart)} – {fmtDate(windowEnd)} (UTC) · Live from{' '}
                <a href="https://status.claude.com" target="_blank" rel="noopener" style={{ color: colorTextAccent }}>status.claude.com</a>
              </span>
            </span>
          }
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              {(['7', '14', '30', '0'] as const).map(p => (
                <Button
                  key={p}
                  variant={period === p && !customStart ? 'primary' : 'normal'}
                  onClick={() => { setPeriod(p); setCustomStart(''); setCustomEnd('') }}
                >
                  {p === '0' ? 'YTD' : p + 'd'}
                </Button>
              ))}
              <Button iconName="refresh" onClick={load} />
            </SpaceBetween>
          }
        >
          Anthropic Service Health
        </Header>

        {/* Active incident banner */}
        <ActiveBanner incidents={allIncidents} />

        {/* KPI strip */}
        <Container header={<Header variant="h2">Summary</Header>}>
          <ColumnLayout columns={4} borders="vertical">
            <KpiCard label="Total Incidents" value={incidents.length} detail={`${Math.round(windowDays)}-day window`} />
            <KpiCard label="Total Downtime" value={formatDuration(totalDowntimeMs)} detail="merged intervals" />
            <KpiCard label="Global MTTR" value={Math.round(globalMttr) + 'm'} detail="avg per incident" />
            <KpiCard label="Full Outages" value={downIncidents.length} detail="down / major outage" />
            <KpiCard label="Global MTTA" value={Math.round(globalMtta) + 'm'} detail="investigating → identified" />
            <KpiCard label="Worst Day" value={worstDay.day} detail={worstDay.count + ' incidents'} />
            <KpiCard label="Incidents / Day" value={(incidents.length / Math.max(windowDays, 1)).toFixed(1)} detail="average" />
            <KpiCard
              label="Longest Incident"
              value={globalLongest ? formatDuration(globalLongest.durationMs) : '-'}
              detail={globalLongest ? globalLongest.title.slice(0, 28) + '…' : ''}
              onClick={globalLongest?.shortlink ? () => window.open(globalLongest.shortlink, '_blank', 'noopener') : undefined}
            />
          </ColumnLayout>
        </Container>

        {/* Entity cards */}
        <EntityCards metrics={entityMetrics} />

        {/* Charts row 1 */}
        <ColumnLayout columns={2}>
          <AvailabilityChart metrics={entityMetrics} />
          <MttrChart metrics={entityMetrics} />
        </ColumnLayout>

        {/* Charts row 2 */}
        <ColumnLayout columns={2}>
          <DailyChart dayCounts={dayCounts} />
          <DowntimeChart metrics={entityMetrics} />
        </ColumnLayout>

        {/* Incident table */}
        <IncidentTable incidents={incidents} />

      </SpaceBetween>
    </div>
  )
}
